import React, {useCallback, useMemo, useState} from 'react';
import _ from 'lodash';
import {useNavigate} from 'react-router';
import OutsideClickHandler from 'react-outside-click-handler';
import {Container, Divider, IconButton,
    Menu, MenuItem, Stack, Toolbar, useMediaQuery, useTheme,
    MenuList, ListItemText,
    ListItemIcon,
    Skeleton} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import {useQueryClient} from '@tanstack/react-query';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import BookIcon from '@mui/icons-material/Book';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Image from './Image.js';
import {openLoginModal} from './Login.js';
import {useUser} from '../util/user.js';
import StyledTooltip from './StyledTooltip.js';
import storage from '../util/storage.js';
import SCButton from './SCButton.js';

const MobileNavbar = props=>{
    let {items, onClose} = props;
    let theme = useTheme();
    let navigate = useNavigate();
    let onClick = useCallback(item=>{
        onClose?.();
        if (item.path)
            return void navigate(item.path);
    }, [onClose, navigate]);
    if (!items?.length)
        return null;
    return <Stack component="nav" position="absolute" sx={{
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: theme.palette.primary.contrastText,
        overflowY: 'scroll',
        zIndex: 1000,
    }}>
      {items.map((item, i)=><React.Fragment key={item.id}>
        {i!=0&&<Divider/>}
        <SCButton text={item.text} onClick={()=>onClick(item)}
          sx={{color: 'white', margin: 1, fontWeight: 'bold'}}/>
      </React.Fragment>)}
    </Stack>;
};

const Navbar = props=>{
    let {items} = props;
    let theme = useTheme();
    let navigate = useNavigate();
    if (!items?.length)
        return null;
    return <Toolbar component="nav" variant="dense"
      sx={{justifyContent: 'center', overflowX: 'auto', gap: 1}}>
      {items.map(item=><StyledTooltip key={item.id} title={item.menu?.length ? <MenuList>
        {item.menu.map((menuItem, i)=><MenuItem key={i} onClick={()=>navigate(menuItem.link)}
          sx={{':hover': {backgroundColor: 'primary.main'}}}>
          <ListItemText>{menuItem.title}</ListItemText>
        </MenuItem>)}
      </MenuList> : null}>
        <SCButton text={item.text} noWrap {...item.path&&{href: item.path}} sx={{
            color: theme.palette.primary.contrastText,
            flexShrink: 0,
            textDecoration: 'none',
            fontWeight: 'bold',
            ':hover': {color: theme.palette.primary.light},
        }}/>
      </StyledTooltip>)}
    </Toolbar>;
};


const MenuSkeleton = ()=><>
  <MenuItem>
    <ListItemText>
      <Skeleton variant="rounded" width={124} height={24}/>
    </ListItemText>
  </MenuItem>
  <MenuItem>
    <ListItemText>
      <Skeleton variant="rounded" width={80} height={24}/>
    </ListItemText>
  </MenuItem>
</>;

const UserMenu = ()=>{
    let theme = useTheme();
    let [user, userLoading] = useUser();
    let [btnEl, setBtnEl] = useState();
    let [menuOpen, setMenuOpen] = useState(false);
    let navigate = useNavigate();
    let queryClient = useQueryClient();
    let onClick = useCallback(()=>setMenuOpen(v=>!v), []);
    let items = useMemo(()=>userLoading ? [] : [
    ...user ? [user.permissions?.includes('admin')&&{
        id: 'admin_blog',
        text: 'ניהול מאמרים',
        icon: <BookIcon fontSize="small"/>,
        onClick: ()=>navigate('/admin/blog'),
    }, user.permissions?.includes('admin')&&{
        id: 'admin_users',
        text: 'ניהול משתמשים',
        icon: <PersonIcon fontSize="small"/>,
        onClick: ()=>navigate('/admin/users'),
    }, user.permissions?.includes('dev')&&{
        id: 'dev_metrics',
        text: 'מטריקות',
        icon: <TroubleshootIcon fontSize="small"/>,
        onClick: ()=>navigate('/admin/metrics'),
    }, {
        id: 'student_area',
        text: 'אזור תלמידים',
        icon: <DashboardIcon fontSize="small"/>,
        onClick: ()=>navigate('/student_area'),
    }, {
        id: 'logout',
        text: 'התנתקות',
        icon: <LogoutIcon fontSize="small"/>,
        onClick: ()=>{
            storage.remove('sc_user');
            storage.remove('sc_token');
            storage.remove('sc_refresh_token');
            queryClient.setQueryData(['user'], {});
            queryClient.invalidateQueries({queryKey: ['user']});
            navigate('/');
        },
    }].filter(Boolean) : [{
        id: 'login',
        text: 'כניסת תלמידים',
        icon: <LoginIcon fontSize="small"/>,
        onClick: ()=>openLoginModal({onDone: ()=>navigate('/student_area')}),
    }]].filter(Boolean), [user, userLoading, queryClient, navigate]);
    return <>
      <IconButton ref={setBtnEl} onClick={onClick}
        sx={{color: theme.palette.primary.contrastText}}>
        <AccountCircleIcon/>
      </IconButton>
      <Menu anchorEl={btnEl} open={menuOpen}
        onClose={()=>setMenuOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}>
        {userLoading ? <MenuSkeleton/> : items.map(item=><MenuItem
          key={item.id} {..._.omit(item, 'id text onClick')}
          onClick={()=>{
              setMenuOpen(false);
              item.onClick?.();
          }}>
          {item.icon&&<ListItemIcon>
            {item.icon}
          </ListItemIcon>}
          <ListItemText>
            {item.text}
          </ListItemText>
        </MenuItem>)}
      </Menu>
    </>;
};

const Header = props=>{
    let {headerMargin} = props;
    let navigate = useNavigate();
    let theme = useTheme();
    let [showMobileNavbar, setShowMobileNavbar] = useState();
    let isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    let items = useMemo(()=>[{
        id: 'home',
        text: 'דף הבית',
        path: '/',
    }, {
        id: 'services',
        text: 'השירותים שלי',
        path: '/services',
        menu: [{
            title: 'פגישת ייעוץ אישית',
            link: '/meeting',
        }, {
            title: 'ליווי עסקי ואסטרטגיה הוליסטית',
            link: '/counseling',
        }, {
            title: 'סדנאות, הרצאות ומעגלים',
            link: '/workshops',
        }, {
            title: 'שיעורים מכושפים אחד על אחד',
            link: '/private_lessons',
        }],
    }, {
        id: 'tarot-spread',
        text: 'מסר יומי',
        path: '/tarot-spread',
    }, {
        id: 'tarotmail-simona',
        text: 'מסר שבועי במייל',
        path: '/tarotmail/simona',
    }, {
        id: 'contact',
        text: 'צור קשר',
        path: '/contact',
    }].filter(Boolean), []);
    return <Stack direction="column" paddingTop={2} marginBottom={headerMargin}
      sx={{backgroundColor: theme.palette.primary.dark}}>
      <Container maxWidth="lg">
        <Toolbar>
          <Stack direction="row" gap={2} justifyContent="space-between"
            width="100%" alignItems="center" paddingBottom={1}>
            {isMobile&&<IconButton onClick={()=>setShowMobileNavbar(v=>!v)}
              sx={{color: theme.palette.primary.contrastText}}>
              {showMobileNavbar ? <CloseIcon/> : <MenuIcon/>}
            </IconButton>}
            <Stack direction="row" alignItems="center" sx={{cursor: 'pointer'}}
              onClick={()=>(navigate('/'), setShowMobileNavbar(false))}>
              <Image imagekit_src="logo-new-2024.png" maxHeight="40px"/>
              <Image imagekit_src="logo-new-2024-text.png" maxHeight="30px"/>
            </Stack>
            <Stack direction="row">
              <UserMenu/>
            </Stack>
          </Stack>
        </Toolbar>
        {!isMobile&&<>
          <Divider/>
          <Navbar items={items}/>
        </>}
        <Divider/>
        {isMobile&&showMobileNavbar&&<OutsideClickHandler
          onOutsideClick={()=>setShowMobileNavbar(false)}>
          <MobileNavbar items={items} onClose={()=>setShowMobileNavbar(false)}/>
        </OutsideClickHandler>}
      </Container>
    </Stack>;
};

export default Header;
